import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Building2,
  FileCode,
  Pencil,
  X,
  Check,
  RotateCcw,
  Loader2,
  AlertTriangle,
  Users,
  Ban,
  Cpu,
} from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchBudgetAnalysis,
  updateTeamAdjustment,
  removeTeamAdjustment,
  updateEpicEstimate,
  removeEpicEstimate,
} from '@/store/slices/scenariosSlice';
import { GlassCard, GlassBadge } from '@/design-system';
import type { BudgetTeamCostDetail, BudgetEpicDemandDetail } from '@/types';

// ============================================================================
// Helpers
// ============================================================================

function formatCurrency(value: number): string {
  if (Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `$${(value / 1_000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function formatCurrencyFull(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

// ============================================================================
// Inline Editor Components
// ============================================================================

interface InlineNumberEditorProps {
  value: number;
  onSave: (val: number) => void;
  onCancel: () => void;
  label: string;
  min?: number;
  max?: number;
}

const InlineNumberEditor: React.FC<InlineNumberEditorProps> = ({
  value,
  onSave,
  onCancel,
  label,
  min = 0,
  max = 10000,
}) => {
  const [inputVal, setInputVal] = useState(String(value));

  const handleSave = () => {
    const num = parseInt(inputVal, 10);
    if (!isNaN(num) && num >= min && num <= max) {
      onSave(num);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="number"
        value={inputVal}
        onChange={(e) => setInputVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') onCancel();
        }}
        className="w-20 px-2 py-0.5 text-sm bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] rounded text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500"
        min={min}
        max={max}
        autoFocus
        aria-label={label}
      />
      <button
        onClick={handleSave}
        className="p-0.5 text-emerald-400 hover:text-emerald-300 transition-colors"
        title="Save"
      >
        <Check className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={onCancel}
        className="p-0.5 text-muted-foreground hover:text-foreground transition-colors"
        title="Cancel"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

interface BudgetAnalysisWidgetProps {
  scenarioId: string;
  programId: string;
  programName: string;
}

const BudgetAnalysisWidget: React.FC<BudgetAnalysisWidgetProps> = ({
  scenarioId,
  programId,
  programName,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { budgetAnalysis, budgetAnalysisLoading, budgetAnalysisError } = useSelector(
    (state: RootState) => state.scenarios
  );

  const [editingTeam, setEditingTeam] = useState<string | null>(null);
  const [editingEpic, setEditingEpic] = useState<string | null>(null);

  const loadBudgetAnalysis = useCallback(() => {
    dispatch(fetchBudgetAnalysis({ scenarioId, programId }));
  }, [dispatch, scenarioId, programId]);

  useEffect(() => {
    loadBudgetAnalysis();
  }, [loadBudgetAnalysis]);

  const handleTeamAdjustmentSave = async (teamId: string, adjustedMemberCount: number) => {
    setEditingTeam(null);
    await dispatch(updateTeamAdjustment({ scenarioId, teamId, adjustedMemberCount }));
    loadBudgetAnalysis();
  };

  const handleTeamAdjustmentReset = async (teamId: string) => {
    await dispatch(removeTeamAdjustment({ scenarioId, teamId }));
    loadBudgetAnalysis();
  };

  const handleEpicEstimateSave = async (epicId: string, adjustedTeamWeeks: number) => {
    setEditingEpic(null);
    await dispatch(updateEpicEstimate({ scenarioId, epicId, adjustedTeamWeeks }));
    loadBudgetAnalysis();
  };

  const handleEpicEstimateReset = async (epicId: string) => {
    await dispatch(removeEpicEstimate({ scenarioId, epicId }));
    loadBudgetAnalysis();
  };

  if (budgetAnalysisLoading && !budgetAnalysis) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        <span className="ml-3 text-muted-foreground">Loading budget analysis...</span>
      </div>
    );
  }

  if (budgetAnalysisError) {
    return (
      <GlassCard className="p-6">
        <div className="flex items-center gap-3 text-red-400">
          <AlertTriangle className="w-5 h-5" />
          <span>{budgetAnalysisError}</span>
        </div>
        <button
          onClick={loadBudgetAnalysis}
          className="mt-4 px-4 py-2 text-sm bg-[var(--bg-tertiary)] text-foreground rounded-lg hover:bg-[var(--bg-hover)] transition-colors"
        >
          Retry
        </button>
      </GlassCard>
    );
  }

  if (!budgetAnalysis) return null;

  const { teamCostProjection, financeBudget, isDemand, variance } = budgetAnalysis;

  const varianceStatusColors = {
    UNDER_BUDGET: { text: 'text-emerald-400', bg: 'bg-emerald-500/20', label: 'Under Budget' },
    NEAR_BUDGET: { text: 'text-amber-400', bg: 'bg-amber-500/20', label: 'Near Budget' },
    OVER_BUDGET: { text: 'text-red-400', bg: 'bg-red-500/20', label: 'Over Budget' },
  };

  const statusStyle = varianceStatusColors[variance.status];

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <GlassCard className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Team Cost</p>
              <p className="text-2xl font-bold mt-1 text-foreground">
                {formatCurrency(teamCostProjection.totalAnnualCost)}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatCurrency(teamCostProjection.monthlyRunRate)}/mo
              </p>
            </div>
            <div className="p-3 rounded-lg bg-blue-500/20">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Labor Budget</p>
              <p className="text-2xl font-bold mt-1 text-foreground">
                {financeBudget.laborBudget > 0
                  ? formatCurrency(financeBudget.laborBudget)
                  : formatCurrency(financeBudget.totalAnnualBudget)}
              </p>
              {financeBudget.nonLaborBudget > 0 && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  +{formatCurrency(financeBudget.nonLaborBudget)} non-labor
                </p>
              )}
            </div>
            <div className="p-3 rounded-lg bg-emerald-500/20">
              <DollarSign className="w-5 h-5 text-emerald-400" />
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">IS Demand</p>
              <p className="text-2xl font-bold mt-1 text-foreground">
                {formatCurrency(isDemand.totalDemandCost)}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isDemand.investibleStructures.length} IS{isDemand.investibleStructures.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-purple-500/20">
              <FileCode className="w-5 h-5 text-purple-400" />
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Variance</p>
              <p className={`text-2xl font-bold mt-1 ${statusStyle.text}`}>
                {variance.teamCostVsBudget >= 0 ? '+' : ''}{formatCurrency(variance.teamCostVsBudget)}
              </p>
              <p className={`text-xs mt-0.5 ${statusStyle.text}`}>
                {variance.teamCostVsBudgetPercent >= 0 ? '+' : ''}{variance.teamCostVsBudgetPercent.toFixed(1)}%
              </p>
            </div>
            <div className={`p-3 rounded-lg ${statusStyle.bg}`}>
              {variance.teamCostVsBudget >= 0 ? (
                <TrendingUp className={`w-5 h-5 ${statusStyle.text}`} />
              ) : (
                <TrendingDown className={`w-5 h-5 ${statusStyle.text}`} />
              )}
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Three-column Detail Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Column 1: Team Cost Projection */}
        <GlassCard className="p-6">
          <h3 className="text-base font-semibold text-foreground mb-1 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-400" />
            Team Cost Projection
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            Annual cost based on team composition
          </p>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {teamCostProjection.teams.map((team: BudgetTeamCostDetail) => {
              const isReleased = team.isReleased === true;
              const isVirtual = team.isVirtual === true;

              return (
                <div
                  key={team.teamId}
                  className={`p-3 rounded-lg transition-colors ${
                    isReleased
                      ? 'bg-red-500/5 border border-red-500/20 opacity-60'
                      : isVirtual
                      ? 'bg-cyan-500/10 border border-cyan-500/30'
                      : team.isAdjusted
                      ? 'bg-amber-500/10 border border-amber-500/30'
                      : 'bg-[var(--bg-tertiary)]/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-medium truncate flex-1 ${
                      isReleased ? 'line-through text-muted-foreground' : 'text-foreground'
                    }`}>
                      {team.teamName}
                    </span>
                    <div className="flex items-center gap-1 ml-2 shrink-0">
                      {isReleased && (
                        <GlassBadge variant="danger" className="text-[10px]">
                          <Ban className="w-2.5 h-2.5 inline mr-0.5" />
                          Released
                        </GlassBadge>
                      )}
                      {isVirtual && (
                        <GlassBadge variant="default" className="text-[10px] !bg-cyan-500/20 !text-cyan-400">
                          <Cpu className="w-2.5 h-2.5 inline mr-0.5" />
                          Virtual
                        </GlassBadge>
                      )}
                      {team.isAdjusted && !isReleased && !isVirtual && (
                        <GlassBadge variant="warning" className="text-[10px]">
                          Adjusted
                        </GlassBadge>
                      )}
                    </div>
                  </div>

                  {/* Cost profile info */}
                  {(team.costProfileName || team.adjustedCostProfileName) && (
                    <div className="text-[10px] text-muted-foreground mb-1">
                      {team.adjustedCostProfileName ? (
                        <span>
                          <span className="line-through">{team.costProfileName}</span>
                          {' \u2192 '}
                          <span className="text-amber-400">{team.adjustedCostProfileName}</span>
                        </span>
                      ) : (
                        team.costProfileName
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      {isReleased ? (
                        <span className="text-red-400 text-[10px]">0 members (released)</span>
                      ) : editingTeam === team.teamId ? (
                        <InlineNumberEditor
                          value={team.adjustedMemberCount ?? team.memberCount}
                          onSave={(val) => handleTeamAdjustmentSave(team.teamId, val)}
                          onCancel={() => setEditingTeam(null)}
                          label={`Adjust ${team.teamName} members`}
                          max={100}
                        />
                      ) : (
                        <span className="flex items-center gap-1">
                          {team.isAdjusted ? (
                            <>
                              <span className="line-through">{team.memberCount}</span>
                              {' \u2192 '}
                              <span className="text-amber-400 font-medium">{team.adjustedMemberCount}</span>
                              {' members'}
                            </>
                          ) : (
                            `${team.memberCount} members`
                          )}
                        </span>
                      )}
                    </span>
                    <span className={`font-medium ${isReleased ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                      {isReleased ? '$0' : formatCurrency(team.annualCost)}/yr
                    </span>
                  </div>
                  {!isReleased && editingTeam !== team.teamId && (
                    <div className="flex items-center gap-1 mt-1.5">
                      {!isVirtual && (
                        <button
                          onClick={() => setEditingTeam(team.teamId)}
                          className="text-[10px] px-1.5 py-0.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded transition-colors"
                          title="Adjust team size"
                        >
                          <Pencil className="w-3 h-3 inline mr-0.5" />
                          Adjust
                        </button>
                      )}
                      {team.isAdjusted && (
                        <button
                          onClick={() => handleTeamAdjustmentReset(team.teamId)}
                          className="text-[10px] px-1.5 py-0.5 text-muted-foreground hover:text-foreground hover:bg-[var(--bg-hover)] rounded transition-colors"
                          title="Reset to actual"
                        >
                          <RotateCcw className="w-3 h-3 inline mr-0.5" />
                          Reset
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-[var(--border-subtle)]">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">Total Annual</span>
              <span className="text-lg font-bold text-foreground">
                {formatCurrency(teamCostProjection.totalAnnualCost)}
              </span>
            </div>
          </div>
        </GlassCard>

        {/* Column 2: Finance Budget */}
        <GlassCard className="p-6">
          <h3 className="text-base font-semibold text-foreground mb-1 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-400" />
            Finance Budget
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            Approved budget allocation for {programName}
          </p>

          <div className="space-y-4">
            <div className="p-4 bg-[var(--bg-tertiary)]/50 rounded-lg">
              <div className="text-xs text-muted-foreground mb-1">Labor Budget</div>
              <div className="text-xl font-bold text-foreground">
                {financeBudget.laborBudget > 0
                  ? formatCurrencyFull(financeBudget.laborBudget)
                  : 'Not set'}
              </div>
            </div>

            <div className="p-4 bg-[var(--bg-tertiary)]/50 rounded-lg">
              <div className="text-xs text-muted-foreground mb-1">Non-Labor Budget</div>
              <div className="text-xl font-bold text-foreground">
                {financeBudget.nonLaborBudget > 0
                  ? formatCurrencyFull(financeBudget.nonLaborBudget)
                  : 'Not set'}
              </div>
            </div>

            <div className="p-4 bg-[var(--bg-tertiary)]/50 rounded-lg">
              <div className="text-xs text-muted-foreground mb-1">Total Annual Budget</div>
              <div className="text-xl font-bold text-foreground">
                {financeBudget.totalAnnualBudget > 0
                  ? formatCurrencyFull(financeBudget.totalAnnualBudget)
                  : 'Not set'}
              </div>
            </div>

            {financeBudget.operationalOverhead > 0 && (
              <div className="p-4 bg-[var(--bg-tertiary)]/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Operational Overhead</div>
                <div className="text-xl font-bold text-foreground">
                  {(financeBudget.operationalOverhead * 100).toFixed(0)}%
                </div>
              </div>
            )}
          </div>

          {financeBudget.totalAnnualBudget === 0 && financeBudget.laborBudget === 0 && (
            <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-xs text-amber-400">
                No budget data configured for this program. Edit the program to set budget values.
              </p>
            </div>
          )}
        </GlassCard>

        {/* Column 3: IS Demand */}
        <GlassCard className="p-6">
          <h3 className="text-base font-semibold text-foreground mb-1 flex items-center gap-2">
            <FileCode className="w-4 h-4 text-purple-400" />
            IS Demand
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            Investible Structure demand in dollars
          </p>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {isDemand.investibleStructures.map((is) => (
              <div
                key={is.isId}
                className="p-3 bg-[var(--bg-tertiary)]/50 rounded-lg"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-sm font-medium text-foreground truncate">
                      {is.isName}
                    </span>
                    <GlassBadge variant="default" className="text-[10px] shrink-0">
                      {is.category}
                    </GlassBadge>
                  </div>
                  <span className="text-sm font-bold text-foreground ml-2">
                    {formatCurrency(is.totalCost)}
                  </span>
                </div>

                <div className="space-y-1.5 ml-2">
                  {is.epics.map((epic: BudgetEpicDemandDetail) => (
                    <div
                      key={epic.epicId}
                      className={`flex items-center justify-between text-xs py-1 px-2 rounded ${
                        epic.isAdjusted
                          ? 'bg-amber-500/10 border border-amber-500/20'
                          : ''
                      }`}
                    >
                      <div className="flex items-center gap-1 flex-1 min-w-0">
                        <span className="text-muted-foreground truncate">
                          {epic.epicName}
                        </span>
                        {epic.isAdjusted && (
                          <GlassBadge variant="warning" className="text-[9px] shrink-0">
                            Adj
                          </GlassBadge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-2 shrink-0">
                        {editingEpic === epic.epicId ? (
                          <InlineNumberEditor
                            value={epic.adjustedTeamWeeks ?? epic.requestedTeamWeeks}
                            onSave={(val) => handleEpicEstimateSave(epic.epicId, val)}
                            onCancel={() => setEditingEpic(null)}
                            label={`Adjust ${epic.epicName} TW`}
                          />
                        ) : (
                          <>
                            <span className="text-muted-foreground">
                              {epic.isAdjusted ? (
                                <>
                                  <span className="line-through">{epic.requestedTeamWeeks}</span>
                                  {'\u2192'}
                                  <span className="text-amber-400">{epic.adjustedTeamWeeks}</span>
                                  {' TW'}
                                </>
                              ) : (
                                `${epic.requestedTeamWeeks} TW`
                              )}
                            </span>
                            <span className="text-foreground font-medium">
                              {formatCurrency(epic.estimatedCost)}
                            </span>
                            <button
                              onClick={() => setEditingEpic(epic.epicId)}
                              className="p-0.5 text-blue-400 hover:text-blue-300 transition-colors"
                              title="Adjust TW estimate"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                            {epic.isAdjusted && (
                              <button
                                onClick={() => handleEpicEstimateReset(epic.epicId)}
                                className="p-0.5 text-muted-foreground hover:text-foreground transition-colors"
                                title="Reset to actual"
                              >
                                <RotateCcw className="w-3 h-3" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {isDemand.investibleStructures.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No EPICs linked to this program
              </div>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-[var(--border-subtle)]">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">Total Demand</span>
              <span className="text-lg font-bold text-foreground">
                {formatCurrency(isDemand.totalDemandCost)}
              </span>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Variance Analysis */}
      <GlassCard className={`p-6 border ${
        variance.status === 'OVER_BUDGET'
          ? 'border-red-500/30'
          : variance.status === 'NEAR_BUDGET'
          ? 'border-amber-500/30'
          : 'border-emerald-500/30'
      }`}>
        <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
          <TrendingUp className="w-4 h-4" />
          Variance Analysis
          <GlassBadge variant={
            variance.status === 'OVER_BUDGET' ? 'danger' :
            variance.status === 'NEAR_BUDGET' ? 'warning' : 'success'
          }>
            {statusStyle.label}
          </GlassBadge>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Team Cost vs Budget</p>
            <p className={`text-lg font-bold ${
              variance.teamCostVsBudget > 0 ? 'text-red-400' : 'text-emerald-400'
            }`}>
              {variance.teamCostVsBudget >= 0 ? '+' : ''}{formatCurrency(variance.teamCostVsBudget)}
            </p>
            <p className={`text-xs ${
              variance.teamCostVsBudget > 0 ? 'text-red-400' : 'text-emerald-400'
            }`}>
              {variance.teamCostVsBudgetPercent >= 0 ? '+' : ''}{variance.teamCostVsBudgetPercent.toFixed(1)}% of budget
            </p>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-1">Demand vs Budget</p>
            <p className={`text-lg font-bold ${
              variance.demandVsBudget > 0 ? 'text-red-400' : 'text-emerald-400'
            }`}>
              {variance.demandVsBudget >= 0 ? '+' : ''}{formatCurrency(variance.demandVsBudget)}
            </p>
            <p className={`text-xs ${
              variance.demandVsBudget > 0 ? 'text-red-400' : 'text-emerald-400'
            }`}>
              {variance.demandVsBudgetPercent >= 0 ? '+' : ''}{variance.demandVsBudgetPercent.toFixed(1)}% of budget
            </p>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-1">Team Cost vs Demand</p>
            {(() => {
              const diff = teamCostProjection.totalAnnualCost - isDemand.totalDemandCost;
              return (
                <>
                  <p className={`text-lg font-bold ${diff > 0 ? 'text-amber-400' : 'text-blue-400'}`}>
                    {diff >= 0 ? '+' : ''}{formatCurrency(diff)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {diff > 0 ? 'Surplus capacity' : 'Demand exceeds capacity'}
                  </p>
                </>
              );
            })()}
          </div>
        </div>
      </GlassCard>
    </div>
  );
};

export default BudgetAnalysisWidget;
